/**
 * useGlobalSearch.js — debounced global search for the ⌘K palette.
 *
 * Owns the three things a suggestion box has to get right and that are easy to get
 * wrong inline in a component:
 *
 *  1. DEBOUNCE — one request per pause in typing, not per keystroke.
 *  2. CANCELLATION — each new request aborts the previous one. Without this,
 *     responses can resolve out of order and a slow response for "atr" overwrites
 *     the already-rendered results for "atrium".
 *  3. STALE-RESPONSE GUARD — the resolved query is compared against the latest
 *     requested one before committing to state, so even an unaborted late response
 *     cannot repaint the list with results for older text.
 *
 * `loading` stays true across the debounce window, so the palette shows one
 * continuous pending state while typing instead of flashing between idle and
 * loading on every character.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { globalSearch, isAbortError } from "../services/search.api";
import { SEARCH_ERROR_MESSAGE } from "../utils/errorMessages";

// Matches the server's own minimum (services/v1/searchMine.v1.service.js): below
// this, a query matches most of what the user owns and is noise rather than a
// suggestion. Checked here too so short queries cost no request at all.
export const MIN_QUERY_LENGTH = 2;

const DEBOUNCE_MS = 250;

const EMPTY = { groups: [], totalResults: 0 };

const useGlobalSearch = (query, { types, limit = 5, enabled = true } = {}) => {
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The query whose request is currently in flight / most recently issued. Read
  // inside the async callback to discard stale responses.
  const latestQuery = useRef("");
  const abortRef = useRef(null);

  // `types` is typically an inline array literal from the caller, so a raw
  // dependency on it would re-run this effect on every render. Depending on the
  // joined string instead keys the effect to the actual value.
  const typesKey = Array.isArray(types) ? types.join(",") : types || "";

  const trimmed = (query || "").trim();

  useEffect(() => {
    // Abort whatever is in flight before deciding what to do next: closing the
    // palette or clearing the box must not leave a request that lands later and
    // repopulates a list the user already dismissed.
    abortRef.current?.abort();
    abortRef.current = null;

    if (!enabled || trimmed.length < MIN_QUERY_LENGTH) {
      latestQuery.current = trimmed;
      setResults(EMPTY);
      setLoading(false);
      setError(null);
      return undefined;
    }

    latestQuery.current = trimmed;
    // Set immediately rather than inside the timer so the pending state covers the
    // debounce window as well as the request itself.
    setLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const data = await globalSearch(trimmed, {
          types: typesKey || undefined,
          limit,
          signal: controller.signal,
        });
        // Stale guard — a response for text the user has since moved past is
        // dropped rather than rendered.
        if (latestQuery.current !== trimmed) return;
        setResults({ groups: data.groups, totalResults: data.totalResults });
        setLoading(false);
      } catch (e) {
        // A cancelled request is a superseded one, not a failure: leave the
        // existing results and pending state to the request that replaced it.
        if (isAbortError(e)) return;
        if (latestQuery.current !== trimmed) return;
        setError(SEARCH_ERROR_MESSAGE);
        setResults(EMPTY);
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    // Cancel both stages on re-run/unmount: the timer if the debounce has not
    // fired yet, the request if it has.
    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [trimmed, typesKey, limit, enabled]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    latestQuery.current = "";
    setResults(EMPTY);
    setLoading(false);
    setError(null);
  }, []);

  return {
    groups: results.groups,
    totalResults: results.totalResults,
    loading,
    error,
    reset,
    // True only once a real search has run and come back empty — distinct from
    // "not searched yet", which must not render a "no results" message.
    isEmpty:
      !loading && !error && trimmed.length >= MIN_QUERY_LENGTH && results.groups.length === 0,
  };
};

export default useGlobalSearch;
